import { css, cx } from "@linaria/core"
import { styled } from "@linaria/react"
import { upperCase } from "lodash-es"
import { type MouseEvent, useState } from "react"


import { Button } from "@/components/Button/Button.tsx"
import { Horizontal } from "@/components/Flex.ts"
import { PrStatus } from "@/components/GitHubIntegration/PrStatus.tsx"
import { MarkdownWrapper } from "@/components/Markdown/MarkdownWrapper.tsx"
import { Principal } from "@/components/Principal.tsx"
import { Spinner } from "@/components/Spinner/Spinner.tsx"
import { Squircle } from "@/components/Squircle/Squircle.tsx"
import { Tooltip } from "@/components/Tooltip/Tooltip.tsx"
import { useDevMode } from "@/hooks/useDevMode.ts"
import {
  GithubFill,
  HeartEmptyColor,
  HeartFill,
  HeartGreenColor,
  Icon16,
  Icon20,
  TimelineMedium
} from "@/icons/Icons.ts"
import { formatDateTime } from "@/lib/date.ts"
import { shortRevisionId } from "@/lib/displayUtils.ts"
import { openLinkInNewWindow } from "@/lib/std.ts"
import { versionTitle } from "@/routes/tasks/TaskViewScene/TaskViewModel.ts"
import * as textStyles from "@/styles/textStyles.ts"
import { ellipsize, truncate } from "@/styles/textStyles.ts"

import type { CPrincipal } from "@/lib/client/generated/TypeDefinitions.ts"
import type { GitHubVersionInfo } from "@/routes/tasks/TaskViewScene/TaskVersionsModel.ts"

export const Version = (props: {
  number: number
  commitId: string
  isCurrent: boolean
  isSuccessful: boolean
  createdAt: number
  createdBy: CPrincipal | null
  title: string
  subtitle?: string | null
  reasoning?: string | null
  isGenerating?: boolean
  limitedWidth?: boolean
  borderless?: boolean
  gitHubVersionInfo?: GitHubVersionInfo | null
  onCheckout: (commitId: string) => Promise<void>
  onMarkSuccessful: (commitId: string, successful: boolean) => Promise<void>
  onClick?: (() => void) | null
}) => {
  const hasOnClick = props.onClick !== undefined && props.onClick !== null
  const limitedWidth = props.limitedWidth === true
  const borderless = props.borderless ?? false
  const existingPr = props.gitHubVersionInfo?.existingPrs?.find(item => item.taskVersion == props.number)

  const CardComponent = (props.isSuccessful && props.isCurrent) ? SuccessfulCard : Card

  const devMode = useDevMode()

  return (
    <MaybeClickable
      className={cx(hasOnClick && Hoverable, borderless && DropShadow)}
      hasOnClick={hasOnClick}
      onClick={props.onClick ? () => props.onClick?.() : undefined}
    >
      <Squircle
        className={cx(VersionContainer, hasOnClick && Hoverable, limitedWidth && VersionContainerLimited, borderless && Borderless)}
      >
        <CardComponent>
          <MarkVersionAsSuccessful
            isCurrent={props.isCurrent}
            isSuccessful={props.isSuccessful}
            onMarkSuccessful={(successful) => props.onMarkSuccessful(props.commitId, successful)}
          />

          <Horizontal justifyContent="center" gap="4px" alignItems="center">
            {props.isCurrent && <CurrentLabel />}
          </Horizontal>

          <Meta>
            <span>
              {versionTitle(props.number)}
            </span>

            <Bullet />

            {devMode && (
              <>
                <span>
                  {shortRevisionId(props.commitId)}
                </span>

                <Bullet />
              </>
            )}

            <span>
              {formatDateTime(props.createdAt)}
            </span>

            {props.createdBy && (
              <>
                <Bullet />
                <Principal principal={props.createdBy} />
              </>
            )}
          </Meta>

          {props.title && <Title>{props.title}</Title>}

          {props.isGenerating
            ? <GeneratingDescription>{ellipsize("Generating summary")}</GeneratingDescription>
            : props.subtitle && <Description>{props.subtitle}</Description>
          }

          <Controls>
            {!props.isCurrent && <Checkout onCheckout={props.onCheckout} commitId={props.commitId} />}

            {props.isCurrent && props.gitHubVersionInfo?.canOpenPr && !existingPr && (
              <Button kind="alternative" size="large" onClick={(e) => {
                e.stopPropagation()
                void props.gitHubVersionInfo?.onCreatePr()
              }}>
                <Horizontal alignItems="center" gap="8px">
                  <GithubFill className={Icon20} />
                  Create pull request
                </Horizontal>
              </Button>
            )}

            {existingPr?.url && (
              <Button kind="alternative" size="large" onClick={(e) => {
                e.stopPropagation()
                openLinkInNewWindow(existingPr?.url)
              }}>
                <Horizontal alignItems="center" gap="8px">
                  <GithubFill className={Icon20} />
                  <PrTitle>{truncate(existingPr?.name, 25)}</PrTitle>
                  <PrNumber>#{existingPr?.number}</PrNumber>
                  <PrStatus kind={existingPr?.state}>{upperCase(existingPr?.state)}</PrStatus>
                </Horizontal>
              </Button>
            )}
          </Controls>
        </CardComponent>
        {props.reasoning && <Outcome text={props.reasoning} />}
      </Squircle>
    </MaybeClickable>
  )
}

const MarkVersionAsSuccessful = (props: {
  isCurrent: boolean
  isSuccessful: boolean
  onMarkSuccessful: (successful: boolean) => Promise<void>
}) => {
  const [inProgress, setInProgress] = useState(false)

  const handleMarkSuccessful = (e: MouseEvent) => {
    e.stopPropagation()
    setInProgress(true)
    void props.onMarkSuccessful?.(!props.isSuccessful).finally(() => setInProgress(false))
  }

  return (
    <HeartContainer>
      {props.isSuccessful
        ? (props.isCurrent
            ? (
              <Button kind="icon" disabled={inProgress} onClick={handleMarkSuccessful}>
                <HeartGreenColor />
              </Button>
            )
            : (
              <Button kind="icon" disabled={inProgress} onClick={handleMarkSuccessful}>
                <HeartFill color="var(--text-color-15)" />
              </Button>
            )
        )
        : (
          <Tooltip content="Mark as successful" align="end">
            <Button kind="icon" disabled={inProgress} onClick={handleMarkSuccessful}>
              <HeartEmptyColor />
            </Button>
          </Tooltip>
        )}
    </HeartContainer>
  )
}

const Checkout = (props: {
  commitId: string
  onCheckout: (commitId: string) => Promise<void>
}) => {
  const [inProgress, setInProgress] = useState(false)

  const handleCheckout = (e: MouseEvent) => {
    e.stopPropagation()

    setInProgress(true)
    void props.onCheckout(props.commitId).finally(() => {
      setInProgress(false)
    })
  }

  return (
    <ControlButton kind="alternative" size="large" disabled={inProgress} onClick={handleCheckout}>
      {inProgress ? <Spinner size={16} /> : <TimelineMedium className={Icon16} />}
      Restore
    </ControlButton>
  )
}

const MaybeClickable = styled(Horizontal)<{
  hasOnClick: boolean
}>`
  width: 100%;
  justify-content: center;
  cursor: ${props => props.hasOnClick ? "pointer" : "default"};
`

const Hoverable = css``

const HeartContainer = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
`

const VersionContainer = css`
  width: 100%;
  padding: 8px;
  filter: none;

  --squircle-border-radius: 44px;
  --squircle-fill: unset;

  mask-image: var(--paint-squircle);

  background: var(--main-color-5);

  &.${Hoverable}:hover {
    background-color: var(--main-color-10);
  }

  &[data-css-paint] {
    background-image: none !important;
  }
`

const Card = styled(Squircle)`
  position: relative;
  padding: 16px;
  filter: none;

  --squircle-border-radius: 40px;
  --squircle-background-color: var(--background-color);
`

const SuccessfulCard = styled(Card)`
  background: radial-gradient(15.76% 31.25% at 95.38% 10%, oklch(95.98% 0.0671 148.26deg / 70%) 0%, oklch(93.83% 0.0462 216.97deg / 0%) 100%), white;
  mask-image: var(--paint-squircle);

  --squircle-fill: unset;
`

const Borderless = css`
  padding: 0;

  --squircle-border-radius: 40px;
  --squircle-background-color: transparent !important;
`

const DropShadow = css`
  filter: drop-shadow(0 4px 50px var(--text-color-5));

  &.${Hoverable}:hover {
    filter: drop-shadow(0 4px 50px var(--text-color-10));
  }

  @container task-versions-tab (max-width: 700px) {
    filter: drop-shadow(0 4px 16px var(--text-color-5));
  }

  @container task-versions-tab (max-width: 530px) {
    filter: none;
  }

  @container task-versions-tab (max-width: 700px) {
    &.${Hoverable}:hover {
      filter: drop-shadow(0 4px 16px var(--text-color-10));
    }
  }

  @container task-versions-tab (max-width: 530px) {
    &.${Hoverable}:hover {
      filter: none;
    }
  }
`

const VersionContainerLimited = css`
  min-width: 240px;
  max-width: 480px;
`

const Title = styled.h2`
  font-size: 18px;
  font-weight: 500;
  line-height: 24px;
  text-align: center;
  text-wrap: balance;

  margin: 4px 0 8px;

  color: var(--text-color);
`

const Meta = styled(Horizontal)`
  ${textStyles.smallestFontSize};
  text-align: center;

  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 4px;

  margin: 4px 0;

  color: var(--text-color-40);
`

const PrTitle = styled(Horizontal)`
  font-weight: 400;
  ${textStyles.smallFontSize};
  text-align: center;

  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 4px;

  color: var(--text-color);
`

const PrNumber = styled(PrTitle)`
  color: var(--text-color-40);
`

const Bullet = styled.span`
  &::after {
    content: "•";
  }
`

const CurrentLabel = styled.div`
  font-size: 9px;
  line-height: 20px;

  padding: 0 6px;

  &::after {
    content: "CURRENT";
  }

  color: var(--success-color);
  background-color: var(--success-color-10);
  border-radius: var(--border-radius-8);
`

const Outcome = styled(MarkdownWrapper)`
  ${textStyles.smallFontSize};

  margin: 22px 24px 14px;

  color: var(--text-color);
`

const Description = styled.div`
  ${textStyles.smallFontSize};
  text-align: center;

  color: var(--text-color-50);
`

const GeneratingDescription = styled.div`
  ${textStyles.smallFontSize};
  text-align: center;

  color: var(--text-color-30);
`

const Controls = styled(Horizontal)`
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px;

  margin-top: 24px;

  &:empty {
    display: none;
  }
`

const ControlButton = styled(Button)`
  gap: 4px;

  color: var(--secondary-text-color);
`
